import React, {
  useContext, useEffect, useReducer, useState,
} from 'react';
import { Save } from '@mui/icons-material';
import { useHistory, useLocation } from 'react-router-dom';
import { Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import { createTicket, searchTags, verifySecurityTicketTag } from 'shared/api/support';
import { Title } from 'shared/components/abstract/interactables';
import FormCard from 'shared/components/FormCard';
import { isBlankString, isValidEmail, isValidPhone } from 'shared/utils';
import { VARIANTS } from 'shared/constants';
import { SideNavContext } from 'shared/hoc/withSideNav';
import { useAuth, useNotification, useRequest } from 'shared/hooks';
import { getBuilding } from 'shared/api/buildings';
import { getUnit } from 'shared/api/units';
import { getTenant } from 'shared/api/tenants';

import { dataReducer } from 'shared/reducers';
import { getTicketPath, getTicketSubjectPath } from 'shared/routes/support';
import TicketForm from './subcomponents/TicketForm';

const initialState = {
  ticketIsCreated: false,
  assignee: null,
  ticketStatus: 'Active',
  building: null,
  unit: null,
  tenant: null,
  priority: 'Normal',
  title: '',
  ticketContact: {
    name: '',
    phone: '',
    email: '',
  },
  text: '',

  minutes: 0,
  ticketTags: [],
};

function reducer(state, { action, payload }) {
  switch (action) {
    case 'ticket':
      return {
        ...state,
        ticketIsCreated: true,
      };

    case 'ticketState':
      return {
        ...state,
        [payload.name]: payload.value,
      };

    case 'ticketContact':
      return {
        ...state,
        ticketContact: {
          ...state.ticketContact,
          [payload.name]: payload.value,
        },
      };

    default:
      return dataReducer(state, { action, payload });
  }
}

function useTicketCreatorState(buildingId, unitId, tenantId) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { request } = useRequest();
  const {
    building, unit, tenant,
  } = state;

  // Sync newBuilding to ?building_id url param.
  useEffect(() => {
    (async () => {
      if (buildingId && (!building || building.id !== buildingId)) {
        const result = await request(getBuilding(buildingId));
        if (result) dispatch({ action: 'ticketState', payload: { name: 'building', value: result } });
      } else if (!buildingId) {
        dispatch({ action: 'ticketState', payload: { name: 'building', value: null } });
      }
    })();
  }, [request, building, buildingId]);

  // Sync newUnit to ?unit_id url param.
  useEffect(() => {
    (async () => {
      if (building && unitId && (!unit || unit.id !== unitId)) {
        const result = await request(getUnit(unitId));
        if (result) dispatch({ action: 'ticketState', payload: { name: 'unit', value: result } });
      } else if (!unitId) {
        dispatch({ action: 'ticketState', payload: { name: 'unit', value: null } });
      }
    })();
  }, [request, building, unit, unitId]);

  // Sync tenant to ?tenant_id url param.
  useEffect(() => {
    (async () => {
      if (tenantId && (!tenant || tenant.id !== tenantId)) {
        const result = await request(getTenant(tenantId));
        if (result) dispatch({ action: 'ticketState', payload: { name: 'tenant', value: result } });
      } else if (!tenantId) {
        dispatch({ action: 'ticketState', payload: { name: 'tenant', value: null } });
      }
    })();
  }, [request, tenant, tenantId]);

  return [state, dispatch];
}

export default function TicketCreator() {
  const history = useHistory();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const tenantId = parseInt(searchParams.get('tenant_id'), 10);
  const unitId = parseInt(searchParams.get('unit_id'), 10);
  const buildingId = parseInt(searchParams.get('building_id'), 10);
  const { user } = useAuth();
  const [state, dispatch] = useTicketCreatorState(buildingId, unitId, tenantId);
  const {
    ticketStatus,
    building,
    priority,
    ticketTags,
    title,
    text,
    minutes,
    assignee,
    ticketContact,
  } = state;

  const { request, isLoading } = useRequest();
  const notification = useNotification();
  const syncSideNav = useContext(SideNavContext);
  const [isSecurityTicketTag, setIsSecurityTicketTag] = useState(false);
  const [stagedTicketTags, setStagedTicketTags] = useState([]);

  useEffect(() => {
    (
      async () => {
        if (ticketTags.length > 0) {
          const response = await Promise
            .all(ticketTags.map((tag) => request(verifySecurityTicketTag(tag.id))));
          if (!response) {
            notification.pushError('Failed to verify security ticket tag');
          } else {
            setIsSecurityTicketTag(response.some((e) => e.is_security_type === true));
          }
        }
      }
    )();
  }, [notification, ticketTags, request]);

  // If the ticket tags are changed
  // then match the staged ticket tags against the available ticket tags

  useEffect(() => {
    const currentTags = ticketTags;
    if (currentTags.length === 0) {
      return;
    }
    (async () => {
      const result = await request(searchTags(''), '');
      if (!result) {
        return;
      }
      const currentMatchedTags = result.rows.filter(
        (tag) => currentTags.some((ticketTag) => tag.id === ticketTag.id),
      ).map(
        (it) => ({ id: it.id, name: it.name }),
      );

      setStagedTicketTags(currentMatchedTags);
    })();
  }, [request, ticketTags]);

  const submitTicket = async (e) => {
    e.preventDefault();

    let params = {
      priority,
      title,
      minutes,
      username: user.username,
      user_id: user.id,
      assignee_username: assignee.username,
      assignee_id: assignee.employeeId,
      status: ticketStatus,
      building_id: buildingId,
      unit_id: unitId,
      tenant_id: tenantId,
      text,
    };

    const tagIds = stagedTicketTags.map((tt) => (tt).id);

    if (!stagedTicketTags || stagedTicketTags.length > 5 || stagedTicketTags.length === 0) {
      notification.pushError('Must select between 1 - 5 tags');
      return;
    }

    if (minutes < 1 && text.length > 0) {
      notification.pushError('New entries must supply minutes');
      return;
    }

    if (isSecurityTicketTag) {
      if (isBlankString(ticketContact.name)) {
        notification.pushError('Must enter a valid security contact name');
        return;
      }
      if (isBlankString(ticketContact.phone) && isBlankString(ticketContact.email)) {
        notification.pushError('Must enter a security contact phone or email');
        return;
      }
      if (!isBlankString(ticketContact.phone) && !isValidPhone(ticketContact.phone)) {
        notification.pushError('Must enter a valid security contact phone number');
        return;
      }
      if (!isBlankString(ticketContact.email) && !isValidEmail(ticketContact.email)) {
        notification.pushError('Must enter a valid security contact email address');
        return;
      }
    }

    params = {
      ...params,
      contact_name: ticketContact?.name,
      contact_phone: ticketContact?.phone,
      contact_email: ticketContact?.email,
      tag_ids: tagIds,
    };

    const result = await request(createTicket(params));
    if (!result) {
      return;
    }

    notification.pushSuccess(`Created ticket ${result.id}`);
    syncSideNav(getTicketPath(result.id), `${result.id} - ${text}`);
    dispatch({ action: 'ticket', payload: result });
    history.push(getTicketPath(result.id));
  };

  const handleTextChange = (name) => (value) => {
    dispatch({ action: 'ticketState', payload: { name, value } });
  };

  const handleTicketContactChange = (name) => (value) => {
    dispatch({ action: 'ticketContact', payload: { name, value } });
  };

  const setAssignee = (newAssignee) => dispatch({ action: 'ticketState', payload: { name: 'assignee', value: newAssignee } });
  const setBuilding = (newBuilding) => {
    dispatch({ action: 'ticketState', payload: { name: 'building', value: newBuilding } });
    history.push(getTicketSubjectPath(
      newBuilding && newBuilding.id,
      null,
      null,
    ));
  };
  const setMinutes = (value) => dispatch({ action: 'ticketState', payload: { name: 'minutes', value } });
  const setTenant = (newTenant) => {
    dispatch({ action: 'ticketState', payload: { name: 'tenant', value: newTenant } });
    history.push(getTicketSubjectPath(
      buildingId,
      unitId,
      newTenant && newTenant.id,
    ));
  };
  const setStagedTicketTagsCall = (tagsArray) => {
    setStagedTicketTags(tagsArray);
    dispatch({ action: 'ticketState', payload: { name: 'ticketTags', value: tagsArray } });
  };
  const setUnit = (newUnit) => {
    dispatch({ action: 'ticketState', payload: { name: 'unit', value: newUnit } });
    history.push(getTicketSubjectPath(
      buildingId,
      newUnit && newUnit.id,
      null,
    ));
  };

  return (
    <form id="ticket-form" onSubmit={submitTicket}>
      <Title title="Create Tickets" />
      <FormCard>
        <Grid container size={12} rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <TicketForm
            state={state}
            stagedTicketTags={stagedTicketTags}
            handleTextChange={handleTextChange}
            handleTicketContactChange={handleTicketContactChange}
            setAssignee={setAssignee}
            setBuilding={setBuilding}
            setMinutes={setMinutes}
            setUnit={setUnit}
            setTenant={setTenant}
            setTicketTags={setStagedTicketTagsCall}
            submitTicket={submitTicket}
            notification={notification}
          />
          <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              startIcon={<Save />}
              disabled={!building || !assignee || !title || !text || !priority
                    || isLoading || !stagedTicketTags || stagedTicketTags?.length === 0}
              form="ticket-form"
              type="submit"
              variant={VARIANTS.contained}
            >
              Create Ticket
            </Button>
          </Grid>
        </Grid>
      </FormCard>
    </form>
  );
}
